'use client';

import classNames from 'classnames';
import { ReactNode, useState, useEffect } from 'react';
import { ReadMoreButton } from '../common/ReadMoreButton';
import Link from 'next/link';
import { Route } from 'next';

interface SidebarWrapperProps {
  children: ReactNode;
  title: string;
  links: {
    id: string;
    label: string;
  }[];
  cta?: {
    text: string;
    href: Route;
  };
  smallTitle?: boolean;
}

const SidebarWrapper: React.FC<SidebarWrapperProps> = ({
  children,
  title,
  links,
  cta = {
    text: 'Request a demo',
    href: '/contact',
  },
  smallTitle,
}) => {
  const [activeSection, setActiveSection] = useState<string>();

  useEffect(() => {
    const handleScroll = () => {
      const sections = links.map((link) => document.getElementById(link.id));
      let closestSection = '';
      let closestOffset = Number.POSITIVE_INFINITY;

      sections.forEach((section) => {
        if (section) {
          const rect = section.getBoundingClientRect();
          const offset = Math.abs(rect.top);

          if (offset < closestOffset) {
            closestOffset = offset;
            closestSection = section.id;
          }
        }
      });

      if (closestSection !== activeSection) {
        setActiveSection(closestSection);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [links, activeSection]);

  return (
    <article className="border-t-2 border-t-background-3 md:flex">
      <aside className="relative z-10 shrink-0 px-4 py-8 md:w-72 md:border-r-2 md:border-r-background md:px-0 md:py-14 md:pl-8 xl:w-[22.5rem] xl:pl-10">
        <div className="sticky top-32">
          <div className="absolute left-full top-20 hidden md:block">
            <svg
              width="27"
              height="81"
              viewBox="0 0 27 81"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="text-background"
            >
              <path
                d="M0 73V8.3202C1.88001 18.6582 7.86035 27.7966 16.5813 33.6578L24.1921 38.773C25.5327 39.6739 25.5327 41.6463 24.1921 42.5472L16.5813 47.6624C7.86036 53.5236 1.88001 62.662 0 73Z"
                fill="white"
              />
              <path
                d="M1 0V8.3202V8.3202C2.23209 18.5866 7.87553 27.8068 16.4574 33.5746L24.3892 38.9054C25.6357 39.7432 25.6357 41.577 24.3892 42.4148L16.4575 47.7456C7.87554 53.5133 2.23209 62.7336 1 73V73V80.5"
                stroke="url(#paint0_linear_5236_52447)"
                strokeWidth="2"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_5236_52447"
                  x1="0.5"
                  y1="40.5"
                  x2="25.5"
                  y2="40.5"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="currentColor" />
                  <stop offset="1" stopColor="currentColor" />
                </linearGradient>
              </defs>
            </svg>
          </div>
          <p
            className={classNames([
              'text-accent xl:whitespace-pre-line',
              smallTitle ? 'pr-14 text-h6' : 'pr-12 text-h5 lg:text-h4',
            ])}
          >
            {title}
          </p>
          <ul className="my-6">
            {links.map((link, index) => (
              <li
                key={index}
                className={classNames([
                  'relative border-l-2',
                  activeSection == link.id
                    ? 'border-l-accent'
                    : 'border-l-background-3 text-secondary',
                ])}
              >
                <Link
                  className="block px-5 py-2 transition-colors hover:text-accent md:text-sm xl:pl-6 xl:pr-12 xl:text-base"
                  href={`#${link.id}`}
                >
                  {link.label}
                </Link>
              </li>
            ))}
          </ul>
          <ReadMoreButton
            text={cta.text}
            href={cta.href}
            variant="outlined"
            slim
          />
        </div>
      </aside>
      <article className="min-w-0 grow">{children}</article>
    </article>
  );
};

export default SidebarWrapper;
